import type { Locale } from './locale';

/** English strings keyed by their Arabic source, exactly like the prototype's T("…"). */
export type Messages = Readonly<Record<string, string>>;

export type Vars = Readonly<Record<string, string | number>>;

/** t("نص") returns the phrase in the active language; t("مرحباً {name}", {name}) fills placeholders. */
export type Translate = (source: string, vars?: Vars) => string;

const missing = new Set<string>();

function interpolate(text: string, vars?: Vars): string {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const v = vars[name];
    return v === undefined ? whole : String(v);
  });
}

/**
 * Builds the translate function for a locale. Arabic returns the source;
 * English looks the source up and falls back to Arabic (as the prototype did).
 */
export function createTranslate(locale: Locale, messages: Messages, opts?: { warn?: boolean }): Translate {
  if (locale === 'ar') return (source, vars) => interpolate(source, vars);
  return (source, vars) => {
    const hit = messages[source];
    if (hit === undefined) {
      if (opts?.warn && !missing.has(source)) {
        missing.add(source);
        console.warn(`[i18n] missing English for: ${source}`);
      }
      return interpolate(source, vars);
    }
    return interpolate(hit, vars);
  };
}

/** Keeps only the given source strings, so a client bundle carries just what it uses. */
export function pickMessages(messages: Messages, sources: readonly string[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const s of sources) {
    const v = messages[s];
    if (v !== undefined) out[s] = v;
  }
  return out;
}

export function mergeMessages(...parts: Messages[]): Messages {
  return Object.freeze(Object.assign({}, ...parts) as Record<string, string>);
}
